import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getComunidades, crearComunidad, updateComunidad, deleteComunidad } from '../services/api';
import { useToast } from '../components/Toast';
import Pagination from '../components/Pagination';
import Button from '../components/ui/Button';

function Comunidades({ user }) {
  const toast = useToast();
  const [comunidades, setComunidades] = useState([]);
  const [comunidadesFiltradas, setComunidadesFiltradas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [busqueda, setBusqueda] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [nuevaComunidad, setNuevaComunidad] = useState({
    nombre: '',
    direccion: '',
    ciudad: '',
    codigo_postal: '',
    num_viviendas: ''
  });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [comunidadToDelete, setComunidadToDelete] = useState(null);
  const [showDeleteSuccess, setShowDeleteSuccess] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editData, setEditData] = useState(null);

  useEffect(() => {
    cargarComunidades();
  }, []);

  useEffect(() => {
    aplicarFiltros();
  }, [comunidades, busqueda]);

  const cargarComunidades = async () => {
    try {
      const response = await getComunidades();
      setComunidades(response.data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const aplicarFiltros = () => {
    let resultado = [...comunidades];

    if (busqueda) {
      const busquedaLower = busqueda.toLowerCase();
      resultado = resultado.filter(com =>
        com.nombre.toLowerCase().includes(busquedaLower) ||
        com.direccion.toLowerCase().includes(busquedaLower) ||
        com.ciudad.toLowerCase().includes(busquedaLower)
      );
    }

    setComunidadesFiltradas(resultado);
    setCurrentPage(1);
  };

  const handleCrearComunidad = async (e) => {
    e.preventDefault();
    try {
      await crearComunidad(nuevaComunidad);
      setShowModal(false);
      setNuevaComunidad({
        nombre: '',
        direccion: '',
        ciudad: '',
        codigo_postal: '',
        num_viviendas: ''
      });
      cargarComunidades();
      toast.success('Comunidad creada correctamente');
    } catch (error) {
      toast.error('Error al crear comunidad: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleEditarComunidad = (com) => {
    setEditData({
      id: com.id,
      nombre: com.nombre,
      direccion: com.direccion,
      ciudad: com.ciudad,
      codigo_postal: com.codigo_postal || '',
      num_viviendas: com.num_pisos || com.num_viviendas || ''
    });
    setShowEditModal(true);
  };

  const handleGuardarEdicion = async (e) => {
    e.preventDefault();
    try {
      await updateComunidad(editData.id, editData);
      setShowEditModal(false);
      setEditData(null);
      cargarComunidades();
      toast.success('Comunidad actualizada correctamente');
    } catch (error) {
      toast.error('Error al actualizar: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleEliminarComunidad = (id, nombre) => {
    setComunidadToDelete({ id, nombre });
    setShowDeleteModal(true);
  };

  const confirmarEliminacion = async () => {
    if (!comunidadToDelete) return;
    try {
      await deleteComunidad(comunidadToDelete.id);
      setShowDeleteModal(false);
      setComunidadToDelete(null);
      setShowDeleteSuccess(true);
      setTimeout(() => setShowDeleteSuccess(false), 3000);
      cargarComunidades();
    } catch (error) {
      setShowDeleteModal(false);
      setComunidadToDelete(null);
      toast.error(error.response?.data?.error || 'Error al eliminar comunidad');
    }
  };

  if (loading) {
    return <div className="loading"><div className="spinner"></div></div>;
  }

  const totalPages = Math.ceil(comunidadesFiltradas.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const comunidadesPaginadas = comunidadesFiltradas.slice(indexOfFirstItem, indexOfLastItem);

  return (
    <div className="container" style={{ paddingTop: 'var(--space-8)', paddingBottom: 'var(--space-10)' }}>
      {/* Header - Compact */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 'var(--space-5)',
        flexWrap: 'wrap',
        gap: 'var(--space-3)'
      }}>
        <div>
          <h1 style={{ fontSize: 'var(--font-3xl)', fontWeight: '600', marginBottom: '2px', letterSpacing: '-0.02em' }}>
            🏢 Comunidades
          </h1>
          <p style={{ color: 'var(--text-muted)', fontSize: 'var(--font-sm)' }}>
            {comunidades.length} en total
          </p>
        </div>
        {user?.rol === 'administrador' && (
          <Button variant="primary" size="md" onClick={() => setShowModal(true)} icon={
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
          }>
            Nueva Comunidad
          </Button>
        )}
      </div>

      {/* Barra de búsqueda - Compact */}
      <div className="card" style={{ marginBottom: 'var(--space-5)', padding: 'var(--space-4)' }}>
        <div className="form-group" style={{ marginBottom: 'var(--space-2)' }}>
          <input
            type="text"
            className="form-control"
            placeholder="🔍 Buscar por nombre, dirección o ciudad..."
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            style={{ fontSize: 'var(--font-sm)', height: '36px' }}
          />
        </div>
        <div style={{ fontSize: 'var(--font-xs)', color: 'var(--text-muted)' }}>
          {comunidadesFiltradas.length} resultados
        </div>
      </div>

      {comunidadesFiltradas.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: '48px' }}>
          <p style={{ color: 'var(--text-secondary)' }}>
            {comunidades.length === 0 ? 'No hay comunidades' : 'No se encontraron comunidades con los filtros aplicados'}
          </p>
        </div>
      ) : (
        <>
          <div className="grid grid-2">
            {comunidadesPaginadas.map(com => (
          <div key={com.id} className="card" style={{ display: 'flex', flexDirection: 'column' }}>
            <div style={{ marginBottom: '16px' }}>
              <h3 style={{ fontSize: '20px', fontWeight: '600', marginBottom: '8px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {com.nombre}
              </h3>
              <p style={{ color: 'var(--text-secondary)', fontSize: '14px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                📍 {com.direccion}, {com.ciudad}
              </p>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '16px' }}>
              {[
                { icon: '👥', label: 'Vecinos', value: com.num_vecinos || 0 },
                { icon: '📝', label: 'Incidencias', value: com.num_incidencias || 0 },
                { icon: '📢', label: 'Anuncios', value: com.num_anuncios || 0 },
              ].map(({ icon, label, value }) => (
                <div key={label} style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  padding: '10px 14px',
                  background: 'linear-gradient(135deg, #2563EB 0%, #1D4ED8 100%)',
                  borderRadius: '12px',
                }}>
                  <span style={{ fontSize: '18px' }}>{icon}</span>
                  <span style={{ fontSize: '12px', fontWeight: '600', color: 'rgba(255,255,255,0.85)', flex: 1 }}>{label}</span>
                  <span style={{ fontSize: '14px', fontWeight: '700', color: '#ffffff', lineHeight: 1 }}>{value}</span>
                </div>
              ))}
            </div>

            {com.presidente_nombre && (
              <p style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '12px' }}>
                👔 Presidente: <strong>{com.presidente_nombre}</strong>
              </p>
            )}

            <div style={{ display: 'flex', gap: '8px', marginTop: 'auto' }}>
              <Link to={`/comunidades/${com.id}`} style={{ flex: 1, textDecoration: 'none' }}>
                <Button variant="primary" size="sm" fullWidth>
                  Ver detalles
                </Button>
              </Link>
              {user?.rol === 'administrador' && (
                <>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => handleEditarComunidad(com)}
                    title="Editar comunidad"
                  >
                    ✏️
                  </Button>
                  <Button
                    variant="danger"
                    size="sm"
                    onClick={() => handleEliminarComunidad(com.id, com.nombre)}
                    title="Eliminar comunidad"
                  >
                    🗑️
                  </Button>
                </>
              )}
            </div>
          </div>
        ))}
      </div>

      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        itemsPerPage={itemsPerPage}
        totalItems={comunidadesFiltradas.length}
        onPageChange={setCurrentPage}
        onItemsPerPageChange={setItemsPerPage}
      />
      </>
      )}

      {/* Modal para crear comunidad */}
      {showModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '24px 16px'
        }} onClick={() => setShowModal(false)}>
          <div className="card" style={{
            maxWidth: '600px',
            width: '100%',
            maxHeight: '90vh',
            overflow: 'auto'
          }} onClick={(e) => e.stopPropagation()}>
            <h2 style={{ fontSize: '20px', fontWeight: '600', marginBottom: '20px' }}>Crear Nueva Comunidad</h2>

            <form onSubmit={handleCrearComunidad}>
              <div className="form-group">
                <label className="form-label">Nombre *</label>
                <input
                  type="text"
                  className="form-control"
                  value={nuevaComunidad.nombre}
                  onChange={(e) => setNuevaComunidad({ ...nuevaComunidad, nombre: e.target.value })}
                  required
                  placeholder="Ej: Comunidad Residencial Los Olivos"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Dirección *</label>
                <input
                  type="text"
                  className="form-control"
                  value={nuevaComunidad.direccion}
                  onChange={(e) => setNuevaComunidad({ ...nuevaComunidad, direccion: e.target.value })}
                  required
                  placeholder="Ej: Calle Principal, 123"
                />
              </div>

              <div className="grid grid-2">
                <div className="form-group">
                  <label className="form-label">Ciudad *</label>
                  <input
                    type="text"
                    className="form-control"
                    value={nuevaComunidad.ciudad}
                    onChange={(e) => setNuevaComunidad({ ...nuevaComunidad, ciudad: e.target.value })}
                    required
                    placeholder="Ej: Madrid"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Código Postal</label>
                  <input
                    type="text"
                    className="form-control"
                    value={nuevaComunidad.codigo_postal}
                    onChange={(e) => setNuevaComunidad({ ...nuevaComunidad, codigo_postal: e.target.value })}
                    placeholder="Ej: 28001"
                  />
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Número de Viviendas</label>
                <input
                  type="number"
                  className="form-control"
                  value={nuevaComunidad.num_viviendas}
                  onChange={(e) => setNuevaComunidad({ ...nuevaComunidad, num_viviendas: e.target.value })}
                  placeholder="Ej: 50"
                  min="0"
                />
              </div>

              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '20px' }}>
                <button type="button" onClick={() => setShowModal(false)} className="btn btn-outline">
                  Cancelar
                </button>
                <button type="submit" className="btn btn-primary">
                  Crear Comunidad
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal de edición */}
      {showEditModal && editData && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '24px 16px' }}
          onClick={() => setShowEditModal(false)}>
          <div className="card" style={{ maxWidth: '600px', width: '100%' }} onClick={(e) => e.stopPropagation()}>
            <h2 style={{ fontSize: '20px', fontWeight: '600', marginBottom: '20px' }}>Editar Comunidad</h2>
            <form onSubmit={handleGuardarEdicion}>
              <div className="form-group">
                <label className="form-label">Nombre *</label>
                <input type="text" className="form-control" value={editData.nombre}
                  onChange={(e) => setEditData({ ...editData, nombre: e.target.value })} required />
              </div>
              <div className="form-group">
                <label className="form-label">Dirección *</label>
                <input type="text" className="form-control" value={editData.direccion}
                  onChange={(e) => setEditData({ ...editData, direccion: e.target.value })} required />
              </div>
              <div className="grid grid-2">
                <div className="form-group">
                  <label className="form-label">Ciudad *</label>
                  <input type="text" className="form-control" value={editData.ciudad}
                    onChange={(e) => setEditData({ ...editData, ciudad: e.target.value })} required />
                </div>
                <div className="form-group">
                  <label className="form-label">Código Postal</label>
                  <input type="text" className="form-control" value={editData.codigo_postal}
                    onChange={(e) => setEditData({ ...editData, codigo_postal: e.target.value })} />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Número de Viviendas</label>
                <input type="number" className="form-control" value={editData.num_viviendas} min="0"
                  onChange={(e) => setEditData({ ...editData, num_viviendas: e.target.value })} />
              </div>
              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '20px' }}>
                <button type="button" onClick={() => setShowEditModal(false)} className="btn btn-outline">Cancelar</button>
                <button type="submit" className="btn btn-primary">Guardar cambios</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal de confirmación de eliminación */}
      {showDeleteModal && comunidadToDelete && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          animation: 'fadeIn 0.3s ease'
        }} onClick={() => { setShowDeleteModal(false); setComunidadToDelete(null); }}>
          <div style={{
            background: 'var(--bg-card)',
            borderRadius: '16px',
            padding: '32px',
            textAlign: 'center',
            maxWidth: '420px',
            width: '90%',
            boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
            border: '1px solid var(--border-color)'
          }} onClick={(e) => e.stopPropagation()}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>⚠️</div>
            <h2 style={{ fontSize: '20px', fontWeight: '700', marginBottom: '12px', color: 'var(--text-color)' }}>
              ¿Eliminar comunidad?
            </h2>
            <p style={{ color: 'var(--text-secondary)', fontSize: '15px', marginBottom: '24px', lineHeight: '1.5' }}>
              ¿Estás seguro de eliminar la comunidad <strong>"{comunidadToDelete.nombre}"</strong>? Esta acción no se puede deshacer.
            </p>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
              <button
                onClick={() => { setShowDeleteModal(false); setComunidadToDelete(null); }}
                className="btn btn-outline"
                style={{ padding: '10px 24px' }}
              >
                Cancelar
              </button>
              <button
                onClick={confirmarEliminacion}
                className="btn btn-danger"
                style={{ padding: '10px 24px' }}
              >
                Eliminar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Popup de eliminación exitosa */}
      {showDeleteSuccess && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          animation: 'fadeIn 0.3s ease'
        }} onClick={() => setShowDeleteSuccess(false)}>
          <div style={{
            background: 'var(--bg-card)',
            borderRadius: '16px',
            padding: '40px 48px',
            textAlign: 'center',
            maxWidth: '400px',
            width: '90%',
            boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
            border: '1px solid var(--border-color)',
            animation: 'slideUp 0.4s ease'
          }} onClick={(e) => e.stopPropagation()}>
            <div style={{ fontSize: '64px', marginBottom: '16px' }}>✅</div>
            <h2 style={{ fontSize: '22px', fontWeight: '700', marginBottom: '8px', color: 'var(--text-color)' }}>
              Eliminada correctamente
            </h2>
            <p style={{ color: 'var(--text-secondary)', fontSize: '15px', marginBottom: '24px', lineHeight: '1.5' }}>
              La comunidad ha sido eliminada correctamente.
            </p>
            <button
              onClick={() => setShowDeleteSuccess(false)}
              className="btn btn-primary"
              style={{ padding: '10px 32px' }}
            >
              Aceptar
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default Comunidades;